import { Divider, Grid, Paper, Typography } from "@material-ui/core";
import React, { useState } from "react";

import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import BurnableTraceabilitySelectionForm from "components/SelectionForm/BurnableTraceabilitySelectionForm";
import InvertedTable from "components/Table/InvertedTable";
import PageTitle from "components/PageTitle/PageTitle";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";
import { excelDownload } from "utils/excelDownload";
import loadingGif from "images/documentLoading.gif";
import useStyles from "./styles";
import LoaderBar from "components/LoaderBar/LoaderBar";

const createColumnsHeaderName = (columns) => {
  return columns.map((column) => {
    const columnHeaderName =
      column.substring(4, 6) + "-" + column.substring(0, 4);
    return columnHeaderName.charAt(0).toUpperCase() + columnHeaderName.slice(1);
  });
};

const COLUMN_LIST = [
  "totals_divider", // divider
  "new_medics",
  "total_medics",
  "medic_growth",
  "new_clients",
  "total_clients",
  "clients_growth",
  "physical_coupons_divider", // divider
  "medics_assigned",
  "is_target",
  "medics_assigned_with_coupon",
  "total_medics_received_coupon",
  "medics_target_with_coupon",
  "Cumplimiento_de_entrega_al_target",
  "digital_coupons_divider", // divider
  "digital_coupon_inscriptions_unique",
  "digital_coupon_inscriptions_total",
  "digital_coupon_sales",
  "virtual_coupons_divider", // divider
  "virtual_coupon_medics_received",
  "virtual_coupon_medics_delivered",
  "virtual_clients_received_by_cycle",
  "virtual_coupon_sales",
];

const COLUMN_HEADERS = {
  totals_divider: { label: "Totales", category: "divider" },
  new_medics: {
    label: "Médicos nuevos",
    category: "data",
  },
  total_medics: {
    label: "Médicos totales",
    category: "data",
  },
  medic_growth: {
    label: "Crecimiento de médicos",
    category: "data",
  },
  new_clients: {
    label: "Clientes nuevos",
    category: "data",
  },
  total_clients: {
    label: "Clientes totales",
    category: "data",
  },
  clients_growth: {
    label: "Crecimiento de clientes",
    category: "data",
  },
  physical_coupons_divider: { label: "Cupones Físicos", category: "divider" },
  medics_assigned: {
    label: "Médicos Asignados",
    category: "data",
  },
  is_target: {
    label: "Médicos Target",
    category: "data",
  },
  medics_assigned_with_coupon: {
    label: "Médicos Asignados que recibieron cupón",
    category: "data",
  },
  total_medics_received_coupon: {
    label: "Médicos que recibieron cupón (total)",
    category: "data",
  },
  medics_target_with_coupon: {
    label: "Médicos Target que recibieron cupón",
    category: "data",
  },
  Cumplimiento_de_entrega_al_target: {
    label: "Cumplimiento de entrega al target",
    category: "data",
  },
  digital_coupons_divider: { label: "Cupones Digitales", category: "divider" },
  digital_coupon_inscriptions_unique: {
    label: "Clientes suscriptos con QR",
    category: "data",
  },
  digital_coupon_inscriptions_total: {
    label: "Suscripciones totales con QR",
    category: "data",
  },
  digital_coupon_sales: {
    label: "Ventas con cupones digitales (QR)",
    category: "data",
  },
  virtual_coupons_divider: { label: "Cupones Virtuales", category: "divider" },
  virtual_coupon_medics_received: {
    label: "Médicos que recibieron cupón virtual",
    category: "data",
  },
  virtual_coupon_medics_delivered: {
    label: "Médicos que entregaron cupón virtual",
    category: "data",
  },
  virtual_clients_received_by_cycle: {
    label: "Clientes que recibieron cupón virtual",
    category: "data",
  },
  virtual_coupon_sales: {
    label: "Ventas con cupones virtuales",
    category: "data",
  },
};

const FIELDS_AS_MILLIONS = [
  // COLUMN_LIST names
];

const FIELDS_AS_PERCENTAGE = [
  "Cumplimiento_de_entrega_al_target",
  "medic_growth",
  "clients_growth",
];

export default function TraceabilityBurnableDashboard() {
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();
  const classes = useStyles();
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialState, setIsInitialState] = useState(true);
  const [excelTotalsData, setExcelTotalsData] = useState([]);

  const generateRowsData = (data) => {
    if (!data) return;
    if (Object.keys(data).length === 0) {
      setColumns([]);
      setRows([]);
      return;
    }
    let columns = Object.keys(data);
    let total_rows = Object.keys(data[columns[0]]);

    setColumns(createColumnsHeaderName(columns));
    generateTableData(total_rows, columns, data);
  };

  const addValueToTable = (data, column, row) => {
    if (FIELDS_AS_MILLIONS.includes(row)) {
      return formatMillions(data[column][row]);
    }
    if (FIELDS_AS_PERCENTAGE.includes(row)) {
      return `${data[column][row].toFixed(2)} %`;
    }
    return data[column][row];
  };

  const generateTableData = (rows, columns, data) => {
    let tableData = {};
    rows.map((row) => {
      let valuesList = columns.map((column) => {
        let category = COLUMN_HEADERS[row].category;
        let rowIncluded = Object.keys(data[column]).includes(row);
        let value =
          rowIncluded && category === "data"
            ? addValueToTable(data, column, row)
            : "";
        return value;
      });
      tableData[row] = valuesList;
    });
    let finalData = COLUMN_LIST.map((row) => {
      let category = COLUMN_HEADERS[row].category;
      return {
        label: COLUMN_HEADERS[row].label,
        category: category,
        data: category === "data" ? tableData[row] : "",
      };
    });

    setRows(finalData);
  };

  const formatMillions = (value) => {
    let lastIndex = value >= 0 ? 4 : 5;
    return String(value / 1000000)
      .slice(0, lastIndex)
      .replace(/\.$/, "")
      .replace(".", ",");
  };

  const renderTables = () => {
    return (
      <>
        <Grid
          item
          xs={12}
          lg={12}
          style={{ marginBottom: "2%", marginTop: "2%", marginLeft: "2.5%" }}
        >
          <Typography variant="h4" className={classes.alignedText}>
            Valores
          </Typography>
          <Divider />
        </Grid>
        <Paper style={{ marginTop: "30px" }}>
          <InvertedTable
            rows={rows}
            columns={columns}
          />
        </Paper>
      </>
    );
  };

  const renderLoadingSection = () => {
    return (
      <Grid
        item
        container
        justifyContent="center"
        xs={12}
        style={{ paddingTop: "2%" }}
      >
        <LoaderBar />
      </Grid>
    );
  };

  const defineSectionToShow = () => {
    if (isLoading) return renderLoadingSection();
    if (isInitialState) return <></>;
    return renderTables();
  };

  return (
    <Grid>
      <PageTitle title="Panel de Trazabilidad Quemables" />
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />
      <Paper elevation={3}>
        <Grid
          container
          xs={12}
          md={12}
          lg={12}
          layout={"row"}
          spacing={"1"}
          style={{ border: "1px" }}
        >
          <BurnableTraceabilitySelectionForm
            generateRowsData={generateRowsData}
            setIsInitialState={setIsInitialState}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            isInitialState={isInitialState}
          />
        </Grid>
      </Paper>
      {defineSectionToShow()}
    </Grid>
  );
}
